import { useCallback, useState } from 'react';
import { CompanyOutput } from '@/api/gql/graphql';
import { useAddCompany, useGetCompanies } from '@/api/hooks/company/useCompany';
import { Checkbox } from '@/components/Common';
import { ColumnDefinition } from '@/components/Ui/DataTable/DataTable.type';
import { CompanyFormValues, useCompanyForm } from '@/hooks/useCompanyForm';
import { useCompanyStore } from '@/store/CompanyStore/CompanyStore';
import { useToast } from '@/hooks/useToast';

export const useCompany = () => {
  const { handleSubmit, register, errors, reset } = useCompanyForm();
  const [search, setSearch] = useState('');
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const {
    openCompanyModal,
    companyListLimit,
    setCompanyListLimit,
    companyListPage,
    setCompanyListPage,
  } = useCompanyStore();

  const { data: companyData, isLoading: isLoadingCompanyData } =
    useGetCompanies({
      name: search,
      limit: companyListLimit,
    });

  const {
    mutate: addCompany,
    isPending: isAddingCompany,
    isSuccess: isAddingCompanySuccess,
  } = useAddCompany();

  const { showToast } = useToast();

  const handleCompanySelection = useCallback((id: string) => {
    setSelectedCompanies((prev) =>
      prev.includes(id)
        ? prev.filter((companyId) => companyId !== id)
        : [...prev, id],
    );
  }, []);

  const columns: ColumnDefinition[] = [
    { field: 'name', header: 'Nom' },
    { field: 'phoneNumber', header: 'Téléphone' },
    { field: 'email', header: 'Email' },
    { field: 'ninea', header: 'Ninea' },
    { field: 'address', header: 'Adresse' },
  ];

  const formattedData = companyData?.map((company: CompanyOutput) => ({
    _id: company._id,
    name: (
      <div className="flex items-center">
        <Checkbox
          onClick={() => handleCompanySelection(company._id)}
          checked={selectedCompanies.includes(company._id)}
          value={company._id}
        />
        <span className="ml-4">
          {company.name}
          <span className="block text-[#616163]">{company.phoneNumber}</span>
        </span>
      </div>
    ),
    address: company.address,
    email: company.email,
    ninea: company.ninea,
    phoneNumber: company.phoneNumber,
  }));

  const handleAddCompany = (data: CompanyFormValues) => {
    addCompany(data, {
      onSuccess: () => {
        showToast('Entreprise ajoutée avec succès', 'success');
        openCompanyModal();
        reset();
      },
      onError: (error) => {
        showToast(error.message, 'error');
      },
    });
  };

  return {
    columns,
    formattedData,
    setSearch,
    handleSubmit,
    register,
    errors,
    reset,
    handleAddCompany,
    isAddingCompany,
    isAddingCompanySuccess,
    isLoadingCompanyData,
    setCompanyListLimit,
    openCompanyModal,
    companyListPage,
    companyListLimit,
    setCompanyListPage,
  };
};
